import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GridConfig, ListDisplayedColumn } from '../../../v2/models/shared/grid/grid.model';
import { CreateBugComponent } from '../create-bug/create-bug.component';
import { DetailsDescriptionComponent } from '../details-description/details-description.component';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';
import { ToastrService } from 'ngx-toastr';
import { PlatformRegionService } from '../../../../app/service/shared/platform-region.service'
import { FormControl } from '@angular/forms';
import { Subject, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith, takeUntil, catchError, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
export interface RequestItem {
  id: number;
  request_number: string;
  type: 'Bugs' | 'Enhancements' | 'general questions';
  subject: string;
  description: string;
  status: string;
  submitted_by: string;
  requested_by: string;
  created_date: Date;
  updated_date: Date;
  given_by_last_name?: string;
  given_by_first_name?: string;
  assigned_category?: string;
}

@Component({
    selector: 'app-request-list',
    templateUrl: './request-list.component.html',
    styleUrl: './request-list.component.scss',
    standalone: false
})
export class RequestListComponent implements OnInit, OnDestroy {
  requestList: RequestItem[] = [];
  selectedTab: number = 0;
  private static readonly TAB_BUGS = 0;
  private static readonly TAB_ENHANCEMENTS = 1;
  private static readonly TAB_GENERAL_QUESTIONS = 2;
  private static readonly TAB_ASSIGNED_REQUEST = 3;
  isLoading: boolean = false;
  searchFilter = new FormControl('');
  statusFilter = new FormControl('');
  filteredRequestList: RequestItem[] = [];
  statusList: string[] = [];
  currentPage: number = 0;
  pageSize: number = 5;
  totalCount: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  private destroy$ = new Subject<void>();
  private tabChange$ = new Subject<number>();
  private pageChange$ = new Subject<void>();

  gridConfig: GridConfig = {
    rowClickable: true,
    hidePaginator: true
  };

  generalQuestionsGridConfig: GridConfig = {
    rowClickable: false,
    hidePaginator: true
  };

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private contactCenterService: ContactCenterApiService,
    private toastr: ToastrService,
    private platformRegionService: PlatformRegionService
  ) { }

  requestColumns = [
    { label: 'ID', key: 'id', type: 'default' },
    { label: 'Subject', key: 'subject', type: 'default', cell_class: 'user-request-cell', header_class: 'user-request-header' },
    { label: 'Status', key: 'status', type: 'default' },
    { label: 'Assignee', key: 'assigned_category', type: 'default' },
    { label: 'Submitted By', key: 'submitted_by', type: 'default' },
    { label: 'Requested By', key: 'requested_by', type: 'default' },
    { label: 'Created Date', key: 'created_date', type: 'date', date_format: 'd MMM yyyy h:mma' },
    { label: 'Updated Date', key: 'updated_date', type: 'date', date_format: 'd MMM yyyy h:mma' }
  ];

  generalQuestionsColumns = [
    { label: 'ID', key: 'id', type: 'default' },
    { label: 'Subject', key: 'subject', type: 'default', cell_class: 'user-request-cell', header_class: 'user-request-header' },
    { label: 'Status', key: 'status', type: 'default' },
    { label: 'Submitted By', key: 'submitted_by', type: 'default' },
    { label: 'Requested By', key: 'requested_by', type: 'default' },
    { label: 'Created Date', key: 'created_date', type: 'date', date_format: 'd MMM yyyy h:mma' },
    { label: 'Updated Date', key: 'updated_date', type: 'date', date_format: 'd MMM yyyy h:mma' }
  ];

  ngOnInit(): void {
    const savedTab = sessionStorage.getItem('requestListSelectedTab');
    this.selectedTab = savedTab ? Number(savedTab) : 0;
    this.setupDynamicSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDynamicSearch(): void {
    merge(
      this.searchFilter.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), tap(() => this.currentPage = 0)),
      this.statusFilter.valueChanges.pipe(tap(() => this.currentPage = 0)),
      this.tabChange$.pipe(tap(() => this.currentPage = 0)),
      this.pageChange$
    ).pipe(
      startWith(null),
      switchMap(() => {
        this.isLoading = true;
        const search = (this.searchFilter.value || '').trim();
        const status = (this.statusFilter.value || '').trim();
        const offset = this.currentPage * this.pageSize;

        if (this.selectedTab === 3) {
          const userId = sessionStorage.getItem('userId');
          const businessUnitCode = sessionStorage.getItem('businessUnitCode') || '';
          return this.contactCenterService.getRequestsByCategory(Number(userId), businessUnitCode).pipe(
            catchError(() => {
              this.toastr.error('Failed to load assigned requests. Please try again.');
              return of([]);
            })
          );
        }

        const requestType = this.selectedTab === 0 ? 'Bugs' : this.selectedTab === 1 ? 'Enhancements' : 'general questions';
        const filterParams: any = {
          business_unit_code: sessionStorage.getItem('businessUnitCode'),
          region: this.platformRegionService.selectedRegion?.name,
          request_type: requestType,
          offset: offset,
          limit: this.pageSize
        };
        if (search) {
          filterParams.search = search;
        }
        if (status) {
          filterParams.status = status;
        }
        return this.contactCenterService.fetchRequestWithLogsByUser(filterParams).pipe(
          catchError(() => {
            this.toastr.error('Failed to load requests. Please try again.');
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (response === null) return;
      this.totalCount = response?.total_count ?? 0;
      const items = response?.data ?? response?.requests ?? (Array.isArray(response) ? response : []);
      this.requestList = items.map(item => ({
        id: item.id,
        request_number: `${item.id}`,
        type: item.request_type as 'Bugs' | 'Enhancements',
        subject: item.user_request,
        description: item.description,
        status: item.status,
        submitted_by: `${item.given_by_first_name || ''} ${item.given_by_last_name || ''}`.trim() || (item.given_by ? `User ${item.given_by}` : ''),
        requested_by: `${item.requested_by_first_name || ''} ${item.requested_by_last_name || ''}`.trim() || (item.requested_by ? `User ${item.requested_by}` : ''),
        created_date: item.created_date ? new Date(item.created_date.split('.')[0] + 'Z') : null,
        updated_date: item.updated_date ? new Date(item.updated_date.split('.')[0] + 'Z') : null,
        assigned_category: item.assigned_category || 'Unassigned'
      }));
      this.statusList = Array.from(new Set(this.requestList.map(r => r.status)));
      this.filteredRequestList = [...this.requestList];
      this.isLoading = false;
    });
  }

  onTabChange(index: number): void {
    this.selectedTab = index;
    this.searchFilter.setValue('', { emitEvent: false });
    this.statusFilter.setValue('', { emitEvent: false });
    this.currentPage = 0;
    this.tabChange$.next(index);
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.pageChange$.next();
  }

  openCreateRequestDialog(): void {
    const dialogRef = this.dialog.open(CreateBugComponent, {
      width: '600px',
      disableClose: true,
      panelClass: 'create-bug-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.type === 'Bugs') {
          this.selectedTab = RequestListComponent.TAB_BUGS;
        } else if (result.type === 'Enhancements') {
          this.selectedTab = RequestListComponent.TAB_ENHANCEMENTS;
        } else if (result.type === 'general questions') {
          this.selectedTab = RequestListComponent.TAB_GENERAL_QUESTIONS;
        }
        this.tabChange$.next(this.selectedTab);
      }
    });
  }

  onRowClick(row: RequestItem): void {
    sessionStorage.setItem('requestListSelectedTab', String(this.selectedTab));
    this.router.navigate(['/app-request-detail', row.id], {
      state: { requestData: row }, skipLocationChange: true
    });
  }
}
